import {
  AlertTriangle,
  ArrowLeft,
  Bot,
  CalendarDays,
  Check,
  Download,
  Expand,
  List,
  LogOut,
  Pencil,
  Plus,
  ReceiptText,
  RefreshCw,
  Search,
  Settings,
  Tags,
  Trash2,
  Upload,
  User as UserIcon,
  UserPlus,
  WalletCards,
  X
} from "lucide-react";
import type React from "react";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { formatUsd, parseUsdToCents } from "../shared/money";
import { dateInTimeZone, daysAgoInTimeZone } from "../shared/dates";
import { accessTokenStorageKey, api, tokenRefreshedEvent } from "./api";
import type { Attachment, Category, MonthlyReport, Source, Tag, Transaction, User } from "./types";

const isStandaloneApp = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  ("standalone" in window.navigator && Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone));

type BootState = "loading" | "setup" | "login" | "ready";
type View = "entry" | "ledger" | "search" | "report" | "settings";
type ReportDimension = "category" | "source" | "member";
type ReportAmountType = "income" | "expense";
type ReportDrilldown = {
  month: string;
  dimension: ReportDimension;
  id: string;
  type: ReportAmountType;
};
type Toast = {
  id: number;
  message: string;
  tone: "ok" | "bad";
};

type MeResponse = {
  user: User;
  household: { id: string; name: string; currency: string; timezone: string };
};

const today = dateInTimeZone();
const currentMonth = today.slice(0, 7);
const colorOptions = [
  "#ff4fa3",
  "#ff8ac4",
  "#fb7185",
  "#ef4444",
  "#dc2626",
  "#f43f5e",
  "#f97316",
  "#fb923c",
  "#facc15",
  "#fde047",
  "#84cc16",
  "#a3e635",
  "#34d399",
  "#10b981",
  "#2dd4bf",
  "#14b8a6",
  "#38bdf8",
  "#0ea5e9",
  "#60a5fa",
  "#3b82f6",
  "#818cf8",
  "#6366f1",
  "#a78bfa",
  "#c084fc",
  "#d946ef",
  "#f472b6",
  "#ec4899",
  "#e879f9",
  "#f9a8d4",
  "#cbd5e1",
  "#94a3b8",
  "#ededed"
];

const navItems: Array<{ view: View; label: string; icon: React.ReactNode; path: string }> = [
  { view: "entry", label: "记账", icon: <ReceiptText size={18} />, path: "/" },
  { view: "ledger", label: "流水", icon: <List size={18} />, path: "/records" },
  { view: "report", label: "月报", icon: <CalendarDays size={18} />, path: "/report" },
  { view: "search", label: "搜索", icon: <Search size={18} />, path: "/search" },
  { view: "settings", label: "设置", icon: <Settings size={18} />, path: "/settings" }
];

export function App() {
  const initialReportDrilldown = pathToReportDrilldown(window.location.pathname, window.location.search);
  const [boot, setBoot] = useState<BootState>("loading");
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(accessTokenStorageKey));
  const [view, setView] = useState<View>(() => pathToView(window.location.pathname));
  const [me, setMe] = useState<MeResponse | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [sources, setSources] = useState<Source[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [report, setReport] = useState<MonthlyReport | null>(null);
  const [month, setMonth] = useState(initialReportDrilldown?.month ?? currentMonth);
  const [reportDrilldown, setReportDrilldown] = useState<ReportDrilldown | null>(initialReportDrilldown);
  const [toast, setToast] = useState<Toast | null>(null);
  const [showSwUpdate, setShowSwUpdate] = useState(false);
  const [appVersion, setAppVersion] = useState("");
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [routeTransactionId, setRouteTransactionId] = useState<string | null>(() =>
    pathToTransactionId(window.location.pathname)
  );
  const [routeTransaction, setRouteTransaction] = useState<Transaction | null>(null);
  const activeUsers = users.filter((user) => user.role !== "bot" && user.is_active);

  useEffect(() => {
    const onTokenRefreshed = (event: Event) => {
      const nextToken = (event as CustomEvent<string>).detail;
      if (nextToken) setToken(nextToken);
    };
    window.addEventListener(tokenRefreshedEvent, onTokenRefreshed);
    return () => window.removeEventListener(tokenRefreshedEvent, onTokenRefreshed);
  }, []);

  useEffect(() => {
    void bootApp();
    if (window.location.pathname === "/ledger") window.history.replaceState({}, "", "/records");
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === "SW_UPDATED" && isStandaloneApp()) setShowSwUpdate(true);
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function checkVersion() {
      try {
        const response = await fetch(`/app-version.json?t=${Date.now()}`, { cache: "no-store" });
        if (!response.ok) return;
        const data = (await response.json()) as { version?: string };
        if (!data.version) return;
        if (!cancelled) setAppVersion(data.version);
      } catch {
        // Version checks are best-effort and should never interrupt bookkeeping.
      }
    }
    void checkVersion();
    const timer = window.setInterval(checkVersion, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const onPopState = () => {
      setView(pathToView(window.location.pathname));
      setReportDrilldown(pathToReportDrilldown(window.location.pathname, window.location.search));
      setRouteTransactionId(pathToTransactionId(window.location.pathname));
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (!token || !routeTransactionId) {
      setRouteTransaction(null);
      return;
    }
    let cancelled = false;
    void api<Transaction>(`/api/transactions/${routeTransactionId}`, { token })
      .then((transaction) => {
        if (!cancelled) setRouteTransaction(transaction);
      })
      .catch((error) => {
        if (!cancelled) {
          showToast(error instanceof Error ? error.message : "找不到这笔流水", "bad");
          setRouteTransactionId(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [routeTransactionId, token]);

  async function bootApp() {
    const status = await api<{ configured: boolean }>("/api/setup/status");
    if (!status.configured) {
      setBoot("setup");
      return;
    }
    const savedToken = localStorage.getItem(accessTokenStorageKey);
    if (!savedToken) {
      const refreshed = await refreshAccessToken();
      if (!refreshed) setBoot("login");
      return;
    }
    setToken(savedToken);
    const ok = await loadAll(savedToken);
    if (ok) {
      setBoot("ready");
      return;
    }
    const refreshed = await refreshAccessToken();
    setBoot(refreshed ? "ready" : "login");
  }

  async function refreshAccessToken() {
    try {
      const result = await api<{ access_token: string }>("/api/auth/refresh", { method: "POST" });
      saveToken(result.access_token);
      await loadAll(result.access_token);
      setBoot("ready");
      return true;
    } catch {
      localStorage.removeItem(accessTokenStorageKey);
      setToken(null);
      return false;
    }
  }

  async function loadAll(accessToken = token, requestedMonth = month) {
    if (!accessToken) return false;
    try {
      const [meData, usersData, categoriesData, sourcesData, tagsData, transactionData, reportData] =
        await Promise.all([
          api<MeResponse>("/api/me", { token: accessToken }),
          api<User[]>("/api/users", { token: accessToken }),
          api<Category[]>("/api/categories", { token: accessToken }),
          api<Source[]>("/api/sources", { token: accessToken }),
          api<Tag[]>("/api/tags", { token: accessToken }),
          api<Transaction[]>("/api/transactions?limit=40", { token: accessToken }),
          api<MonthlyReport>(`/api/reports/monthly?month=${requestedMonth}`, { token: accessToken })
        ]);
      setMe(meData);
      setUsers(usersData);
      setCategories(categoriesData);
      setSources(sourcesData);
      setTags(tagsData);
      setTransactions(transactionData);
      setReport(reportData);
      const latestToken = localStorage.getItem(accessTokenStorageKey);
      if (latestToken && latestToken !== accessToken) setToken(latestToken);
      return true;
    } catch (error) {
      if (error instanceof Error) showToast(error.message, "bad");
      return false;
    }
  }

  function showToast(message: string, tone: "ok" | "bad" = "ok") {
    setToast({ id: Date.now(), message, tone });
  }

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  function saveToken(nextToken: string) {
    localStorage.setItem(accessTokenStorageKey, nextToken);
    setToken(nextToken);
  }

  async function logout() {
    await api("/api/auth/logout", { method: "POST", token }).catch(() => null);
    localStorage.removeItem(accessTokenStorageKey);
    setToken(null);
    setBoot("login");
  }

  function navigate(nextView: View) {
    setView(nextView);
    setRouteTransactionId(null);
    setReportDrilldown(null);
    const nextPath = viewToPath(nextView);
    if (window.location.pathname !== nextPath) window.history.pushState({}, "", nextPath);
  }

  function openReportDrilldown(dimension: ReportDimension, id: string, type: ReportAmountType) {
    const nextDrilldown = { month, dimension, id, type };
    setView("report");
    setRouteTransactionId(null);
    setRouteTransaction(null);
    setReportDrilldown(nextDrilldown);
    const path = reportDrilldownPath(nextDrilldown);
    if (`${window.location.pathname}${window.location.search}` !== path) window.history.pushState({}, "", path);
  }

  function closeReportDrilldown() {
    setReportDrilldown(null);
    if (window.location.pathname !== "/report") window.history.pushState({}, "", "/report");
  }

  function openTransaction(transaction: Transaction) {
    setView("ledger");
    setRouteTransaction(transaction);
    setRouteTransactionId(transaction.id);
    const path = `/records/${transaction.id}`;
    if (window.location.pathname !== path) window.history.pushState({}, "", path);
  }

  function closeTransaction() {
    setRouteTransactionId(null);
    setRouteTransaction(null);
    if (window.location.pathname.startsWith("/records/")) window.history.pushState({}, "", "/records");
  }

  if (boot === "loading") return <Splash />;
  if (boot === "setup") return <SetupScreen onDone={saveTokenAndLoad} />;
  if (boot === "login") return <LoginScreen onDone={saveTokenAndLoad} />;

  async function saveTokenAndLoad(nextToken: string) {
    saveToken(nextToken);
    await loadAll(nextToken);
    setBoot("ready");
  }

  const membersReady = activeUsers.length > 0 && categories.length > 0 && sources.length > 0;

  return (
    <div className="app-shell">
      <header className="nav">
        <div className="nav-inner">
          <button className="brand" type="button" onClick={() => navigate("entry")}>
            <span className="brand-mark" aria-hidden="true" />
            Our Money
            <span className="brand-page">{me?.household.name ?? "记账"}</span>
          </button>
          <div className="nav-links" aria-label="主导航">
            {navItems.map((item) => (
              <button
                className={view === item.view ? "active" : ""}
                key={item.view}
                onClick={() => navigate(item.view)}
                type="button"
              >
                {item.icon} {item.label}
              </button>
            ))}
          </div>
          <div className="user-menu">
            {isStandaloneApp() && (
              <button className="icon-btn" type="button" onClick={() => window.location.reload()} aria-label="刷新页面" title="刷新页面">
                <RefreshCw size={16} />
              </button>
            )}
            <button
              className="user-menu-trigger"
              type="button"
              onClick={() => setUserMenuOpen((current) => !current)}
              aria-label="当前用户"
              aria-expanded={userMenuOpen}
            >
              <UserIcon size={16} />
              <span>{me?.user.display_name ?? "User"}</span>
            </button>
            {userMenuOpen && (
              <div className="user-menu-popover">
                <p className="mono">{me?.user.display_name ?? "User"}</p>
                <button type="button" onClick={logout}>
                  <LogOut size={15} /> 退出登录
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main>
        <section className="hero compact">
          <div className="shell">
            <h1>
              今天这笔，
              <br />
              记到 <span className="accent">Our Money</span>.
            </h1>
            <p className="lede">两个人的收入、支出、标签和月度复盘都在一个干净的账本里。</p>
          </div>
        </section>

        {!membersReady && (
          <section className="section-tight">
            <div className="shell">
              <p className="notice">基础资料还没准备好。请到设置里添加第二个用户、分类或资金来源。</p>
            </div>
          </section>
        )}

        {view === "entry" && (
          <EntryView
            token={token}
            currentUserId={me?.user.id ?? ""}
            users={activeUsers}
            categories={categories.filter((item) => !item.archived_at)}
            sources={sources.filter((item) => !item.archived_at)}
            tags={tags.filter((item) => !item.archived_at)}
            transactions={transactions}
            report={report}
            onChanged={() => loadAll()}
            onOpenTransaction={openTransaction}
            onMessage={showToast}
          />
        )}
        {view === "search" && (
          <SearchView
            token={token}
            users={activeUsers}
            categories={categories}
            sources={sources}
            tags={tags}
            onChanged={() => loadAll()}
            onOpenTransaction={openTransaction}
            onMessage={showToast}
          />
        )}
        {view === "ledger" && (
          <LedgerView
            token={token}
            onOpenTransaction={openTransaction}
            onMessage={showToast}
          />
        )}
        {view === "report" && (
          <ReportView
            token={token}
            month={month}
            report={report}
            drilldown={reportDrilldown}
            setMonth={async (nextMonth) => {
              setMonth(nextMonth);
              await loadAll(token, nextMonth);
            }}
            onOpenDrilldown={openReportDrilldown}
            onCloseDrilldown={closeReportDrilldown}
            onOpenTransaction={openTransaction}
            onMessage={showToast}
          />
        )}
        {view === "settings" && (
          <SettingsView
            token={token}
            me={me}
            users={users}
            categories={categories}
            sources={sources}
            tags={tags}
            onChanged={() => loadAll()}
            onMessage={showToast}
          />
        )}
      </main>
      {routeTransaction && (
        <TransactionDetailModal
          transaction={routeTransaction}
          token={token}
          users={activeUsers}
          categories={categories.filter((item) => !item.archived_at)}
          sources={sources.filter((item) => !item.archived_at)}
          tags={tags.filter((item) => !item.archived_at)}
          onClose={closeTransaction}
          onChanged={async (nextTransactionId) => {
            await loadAll();
            const transactionId = nextTransactionId ?? routeTransactionId;
            if (transactionId) {
              const fresh = await api<Transaction>(`/api/transactions/${transactionId}`, { token });
              if (nextTransactionId) {
                setRouteTransactionId(fresh.id);
                window.history.replaceState({}, "", `/records/${fresh.id}`);
              }
              setRouteTransaction(fresh);
            }
          }}
          onDeleted={async () => {
            await loadAll();
            closeTransaction();
          }}
          onMessage={showToast}
        />
      )}
      <nav className="mobile-tabbar" aria-label="主导航">
        {navItems.map((item) => (
          <button
            className={view === item.view ? "active" : ""}
            key={item.view}
            onClick={() => navigate(item.view)}
            type="button"
          >
            {item.icon}
            <span>{item.label}</span>
          </button>
        ))}
      </nav>
      {toast && <ToastView key={toast.id} toast={toast} />}
      {showSwUpdate && (
        <ConfirmModal
          title="新版本已准备好"
          body="服务器上已经有新版 Our Money。刷新后会载入最新界面，当前已保存的数据不会丢。"
          confirmLabel="刷新更新"
          onCancel={() => setShowSwUpdate(false)}
          onConfirm={() => window.location.reload()}
          tone="update"
        />
      )}
      <footer className="footer">
        <div className="shell footer-row">
          <span>
            <span className="brand-mark" aria-hidden="true" /> Our Money
          </span>
          <span className="mono">© 2026 dkt · v{appVersion || "dev"}</span>
        </div>
      </footer>
    </div>
  );
}

function LedgerView(props: {
  token: string | null;
  onOpenTransaction: (transaction: Transaction) => void;
  onMessage: (message: string, tone?: "ok" | "bad") => void;
}) {
  const [items, setItems] = useState<Transaction[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const limit = 30;

  useEffect(() => {
    setItems([]);
    setOffset(0);
    setHasMore(true);
    void loadPage(0, true);
  }, [props.token]);

  useEffect(() => {
    const reload = () => void loadPage(0, true);
    window.addEventListener("our-money:transactions-changed", reload);
    return () => window.removeEventListener("our-money:transactions-changed", reload);
  }, [props.token, loading]);

  async function loadPage(nextOffset = offset, replace = false) {
    if (!props.token || loading) return;
    setLoading(true);
    try {
      const page = await api<Transaction[]>(`/api/transactions?limit=${limit}&offset=${nextOffset}`, {
        token: props.token
      });
      setItems((current) => (replace ? page : [...current, ...page]));
      setOffset(nextOffset + page.length);
      setHasMore(page.length === limit);
    } catch (error) {
      props.onMessage(error instanceof Error ? error.message : "加载流水失败", "bad");
    } finally {
      setLoading(false);
    }
  }

  const groups = useMemo(() => groupTransactionsByMonth(items), [items]);

  return (
    <section className="section">
      <div className="shell ledger-layout">
        <h2>Records</h2>
        {groups.length === 0 && !loading && <p className="muted">还没有流水。</p>}
        <div className="ledger-groups">
          {groups.map((group) => (
            <section className="ledger-month" key={group.month}>
              <div className="ledger-month-head">
                <h3>{formatMonthLabel(group.month)}</h3>
                <span className="mono ledger-month-total">
                  收入 <span className="income">{formatUsd(group.income)}</span> · 支出{" "}
                  <span className="expense">{formatUsd(group.expense)}</span>
                </span>
              </div>
              <TransactionList transactions={group.items} title="" onSelect={props.onOpenTransaction} />
            </section>
          ))}
        </div>
        {hasMore && (
          <button className="btn ledger-more" type="button" onClick={() => loadPage()} disabled={loading}>
            {loading ? "加载中" : "加载更早流水"}
          </button>
        )}
      </div>
    </section>
  );
}

function ToastView({ toast }: { toast: Toast }) {
  return (
    <div className={`toast ${toast.tone}`} role="status" aria-live="polite">
      {toast.message}
    </div>
  );
}

function Splash() {
  return (
    <main className="center-screen">
      <span className="brand-mark large" />
      <p className="mono">OUR MONEY LOADING</p>
    </main>
  );
}

function SetupScreen({ onDone }: { onDone: (token: string) => void }) {
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      const result = await api<{ access_token: string }>("/api/setup", {
        method: "POST",
        body: {
          householdName: String(form.get("householdName") || "Our Money"),
          displayName: String(form.get("displayName") || ""),
          email: String(form.get("email") || ""),
          password: String(form.get("password") || "")
        }
      });
      onDone(result.access_token);
    } catch (err) {
      setError(err instanceof Error ? err.message : "初始化失败");
    }
  }

  return (
    <AuthFrame eyebrow="FIRST RUN" title="创建 Our Money 账本">
      <form className="form-grid" onSubmit={submit}>
        <label>
          账本名称
          <input name="householdName" defaultValue="Our Money" />
        </label>
        <label>
          你的名字
          <input name="displayName" autoComplete="name" required />
        </label>
        <label>
          邮箱
          <input name="email" type="email" autoComplete="email" required />
        </label>
        <label>
          密码
          <input name="password" type="password" autoComplete="new-password" minLength={8} required />
        </label>
        {error && <p className="notice bad">{error}</p>}
        <button className="btn btn-primary" type="submit">
          <Plus size={16} /> 创建账本
        </button>
      </form>
    </AuthFrame>
  );
}

function LoginScreen({ onDone }: { onDone: (token: string) => void }) {
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      const result = await api<{ access_token: string }>("/api/auth/login", {
        method: "POST",
        body: {
          email: String(form.get("email") || ""),
          password: String(form.get("password") || "")
        }
      });
      onDone(result.access_token);
    } catch (err) {
      setError(err instanceof Error ? err.message : "登录失败");
    }
  }

  return (
    <AuthFrame eyebrow="SIGN IN" title="回到 Our Money">
      <form className="form-grid" onSubmit={submit}>
        <label>
          邮箱
          <input name="email" type="email" autoComplete="email" required />
        </label>
        <label>
          密码
          <input name="password" type="password" autoComplete="current-password" required />
        </label>
        {error && <p className="notice bad">{error}</p>}
        <button className="btn btn-primary" type="submit">
          <WalletCards size={16} /> 登录
        </button>
      </form>
    </AuthFrame>
  );
}

function AuthFrame({ eyebrow, title, children }: { eyebrow: string; title: string; children: React.ReactNode }) {
  return (
    <main className="auth-page">
      <div className="auth-panel">
        <a className="brand" href="/">
          <span className="brand-mark" />
          Our Money
        </a>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {children}
      </div>
    </main>
  );
}

function EntryView(props: {
  token: string | null;
  currentUserId: string;
  users: User[];
  categories: Category[];
  sources: Source[];
  tags: Tag[];
  transactions: Transaction[];
  report: MonthlyReport | null;
  onChanged: () => void;
  onOpenTransaction: (transaction: Transaction) => void;
  onMessage: (message: string, tone?: "ok" | "bad") => void;
}) {
  const [type, setType] = useState<"expense" | "income">("expense");
  const recentTransactions = props.transactions.filter((transaction) => transaction.occurred_on >= daysAgo(3));

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const files = form.getAll("attachments").filter((file): file is File => file instanceof File && file.size > 0);
    try {
      const result = await api<{ id: string }>("/api/transactions", {
        token: props.token,
        method: "POST",
        body: {
          type,
          occurred_on: String(form.get("occurred_on")),
          amount_cents: parseUsdToCents(String(form.get("amount"))),
          category_id: String(form.get("category_id")),
          source_id: String(form.get("source_id")),
          spender_user_id: String(form.get("spender_user_id")),
          name: String(form.get("name") || "") || null,
          note: String(form.get("note") || "") || null,
          tag_ids: form.getAll("tag_ids").map(String)
        }
      });
      let currentTransactionId = result.id;
      for (const file of files) {
        const attachment = new FormData();
        attachment.set("file", file);
        const uploaded = await api<{ id: string; transaction_id: string }>(`/api/transactions/${currentTransactionId}/attachments`, {
          token: props.token,
          method: "POST",
          body: attachment
        });
        currentTransactionId = uploaded.transaction_id;
      }
      formElement.reset();
      props.onMessage("已记账");
      props.onChanged();
    } catch (error) {
      props.onMessage(error instanceof Error ? error.message : "保存失败", "bad");
    }
  }

  return (
    <section className="section">
      <div className="shell dashboard-grid">
        <form className="entry-panel" onSubmit={submit}>
          <h2>Quick entry</h2>
          <div className="segmented">
            <button className={type === "expense" ? "selected" : ""} type="button" onClick={() => setType("expense")}>
              支出
            </button>
            <button className={type === "income" ? "selected" : ""} type="button" onClick={() => setType("income")}>
              收入
            </button>
          </div>
          <label className="amount-input">
            金额
            <input name="amount" inputMode="decimal" placeholder="24.50" required />
          </label>
          <div className="field-row">
            <label>
              日期
              <input name="occurred_on" type="date" defaultValue={today} required />
            </label>
            <label>
              谁花/收
              <select name="spender_user_id" defaultValue={props.currentUserId} required>
                {props.users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.display_name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="field-row">
            <label>
              分类
              <select name="category_id" required>
                {props.categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              来源
              <select name="source_id" required>
                {props.sources.map((source) => (
                  <option key={source.id} value={source.id}>
                    {source.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label>
            名字
            <input name="name" placeholder="可选，例如 Costco / 工资" />
          </label>
          <label>
            备注
            <textarea name="note" rows={3} placeholder="可选" />
          </label>
          <div className="form-field">
            <span>附件</span>
            <FilePicker name="attachments" multiple label="选择附件" />
          </div>
          <fieldset className="tag-field">
            <legend>标签</legend>
            {props.tags.map((tag) => (
              <label key={tag.id} className="check-chip">
                <input type="checkbox" name="tag_ids" value={tag.id} />
                {tag.name}
              </label>
            ))}
          </fieldset>
          <button className="btn btn-primary" type="submit">
            <Plus size={16} /> 保存这一笔
          </button>
        </form>

        <div className="side-stack">
          <SummaryStrip report={props.report} />
          <TransactionList transactions={recentTransactions} title="过去三天流水" onSelect={props.onOpenTransaction} />
        </div>
      </div>
    </section>
  );
}

function SearchView(props: {
  token: string | null;
  users: User[];
  categories: Category[];
  sources: Source[];
  tags: Tag[];
  onChanged: () => void;
  onOpenTransaction: (transaction: Transaction) => void;
  onMessage: (message: string, tone?: "ok" | "bad") => void;
}) {
  const [results, setResults] = useState<Transaction[]>([]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const params = new URLSearchParams();
    for (const [key, value] of form.entries()) {
      const text = String(value).trim();
      if (!text) continue;
      if (key === "min_amount" || key === "max_amount") {
        try {
          params.set(key === "min_amount" ? "min_cents" : "max_cents", String(parseUsdToCents(text)));
        } catch {
          props.onMessage("金额格式不正确，请输入 11.12 这样的数字", "bad");
          return;
        }
      } else {
        params.set(key, text);
      }
    }
    try {
      setResults(await api<Transaction[]>(`/api/transactions?${params.toString()}`, { token: props.token }));
    } catch (error) {
      props.onMessage(error instanceof Error ? error.message : "搜索失败", "bad");
    }
  }

  return (
    <section className="section">
      <div className="shell">
        <form className="filter-bar" onSubmit={submit}>
          <label>
            关键词
            <input name="q" placeholder="名字或备注" />
          </label>
          <label>
            类型
            <select name="type">
              <option value="">全部</option>
              <option value="expense">支出</option>
              <option value="income">收入</option>
            </select>
          </label>
          <label>
            分类
            <select name="category_id">
              <option value="">全部</option>
              {props.categories.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            谁
            <select name="spender_user_id">
              <option value="">全部</option>
              {props.users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.display_name}
                </option>
              ))}
            </select>
          </label>
          <label>
            来源
            <select name="source_id">
              <option value="">全部</option>
              {props.sources.map((source) => (
                <option key={source.id} value={source.id}>
                  {source.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            标签
            <select name="tag_id">
              <option value="">全部</option>
              {props.tags.map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            状态
            <select name="status" defaultValue="active">
              <option value="active">当前</option>
              <option value="inactive">历史/已删除</option>
              <option value="all">全部</option>
            </select>
          </label>
          <label>
            开始
            <input name="from" type="date" />
          </label>
          <label>
            结束
            <input name="to" type="date" />
          </label>
          <label>
            最小金额
            <input name="min_amount" inputMode="decimal" placeholder="例如 11.12" />
          </label>
          <label>
            最大金额
            <input name="max_amount" inputMode="decimal" placeholder="例如 500.00" />
          </label>
          <button className="btn btn-primary" type="submit">
            <Search size={16} /> 搜索
          </button>
        </form>
        <TransactionList transactions={results} title="搜索结果" emptyLabel="还没有结果" onSelect={props.onOpenTransaction} />
      </div>
    </section>
  );
}

function ReportView(props: {
  token: string | null;
  month: string;
  report: MonthlyReport | null;
  drilldown: ReportDrilldown | null;
  setMonth: (month: string) => Promise<void>;
  onOpenDrilldown: (dimension: ReportDimension, id: string, type: ReportAmountType) => void;
  onCloseDrilldown: () => void;
  onOpenTransaction: (transaction: Transaction) => void;
  onMessage: (message: string, tone?: "ok" | "bad") => void;
}) {
  const report = props.report;
  if (props.drilldown) {
    return (
      <ReportDrilldownView
        token={props.token}
        report={report}
        drilldown={props.drilldown}
        onBack={props.onCloseDrilldown}
        onOpenTransaction={props.onOpenTransaction}
        onMessage={props.onMessage}
      />
    );
  }
  return (
    <section className="section">
      <div className="shell report-layout">
        <div className="report-head">
          <h2>Monthly report</h2>
          <input type="month" value={props.month} onChange={(event) => props.setMonth(event.target.value)} />
        </div>
        <SummaryStrip report={report} />
        {report && (
          <div className="report-columns">
            <ReportTable title="分类" dimension="category" rows={report.by_category} onOpen={props.onOpenDrilldown} />
            <ReportTable title="来源" dimension="source" rows={report.by_source} onOpen={props.onOpenDrilldown} />
            <ReportTable title="成员" dimension="member" rows={report.by_member} onOpen={props.onOpenDrilldown} />
          </div>
        )}
      </div>
    </section>
  );
}

function ReportDrilldownView(props: {
  token: string | null;
  report: MonthlyReport | null;
  drilldown: ReportDrilldown;
  onBack: () => void;
  onOpenTransaction: (transaction: Transaction) => void;
  onMessage: (message: string, tone?: "ok" | "bad") => void;
}) {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const bucket = getReportRows(props.report, props.drilldown.dimension).find((row) => row.id === props.drilldown.id);
  const title = bucket?.name ?? reportDimensionLabel(props.drilldown.dimension);
  const cents = props.drilldown.type === "income" ? bucket?.income_cents ?? 0 : bucket?.expense_cents ?? 0;
  const memberBreakdown = useMemo(() => summarizeByMember(transactions), [transactions]);
  const filteredTransactions = useMemo(
    () => transactions.filter((transaction) => !selectedMemberId || transaction.spender_user_id === selectedMemberId),
    [transactions, selectedMemberId]
  );

  useEffect(() => {
    let cancelled = false;
    async function loadDrilldown() {
      if (!props.token) return;
      setLoading(true);
      setSelectedMemberId(null);
      try {
        const rows = await fetchReportTransactions(props.token, props.drilldown);
        if (!cancelled) setTransactions(rows);
      } catch (error) {
        if (!cancelled) props.onMessage(error instanceof Error ? error.message : "月报明细加载失败", "bad");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void loadDrilldown();
    return () => {
      cancelled = true;
    };
  }, [props.token, props.drilldown.month, props.drilldown.dimension, props.drilldown.id, props.drilldown.type]);

  return (
    <section className="section">
      <div className="shell report-layout">
        <div className="report-drilldown-head">
          <button className="btn icon-only" type="button" onClick={props.onBack} aria-label="返回月报" title="返回月报">
            <ArrowLeft size={17} />
          </button>
          <div>
            <h2>{formatMonthLabel(props.drilldown.month)} · {reportDimensionLabel(props.drilldown.dimension)}</h2>
            <h1>{title}</h1>
          </div>
          <strong className={props.drilldown.type}>
            {props.drilldown.type === "income" ? "+" : "-"}{formatUsd(cents)}
          </strong>
        </div>
        {loading ? (
          <p className="muted">加载中...</p>
        ) : (
          <>
            <MemberBreakdown
              rows={memberBreakdown}
              type={props.drilldown.type}
              title={props.drilldown.type === "income" ? "谁收" : "谁花"}
              selectedMemberId={selectedMemberId}
              onSelect={(memberId) => setSelectedMemberId((current) => (current === memberId ? null : memberId))}
            />
            <TransactionList transactions={filteredTransactions} title={selectedMemberId ? "筛选流水" : "组成流水"} emptyLabel="还没有记录" onSelect={props.onOpenTransaction} />
          </>
        )}
      </div>
    </section>
  );
}

function MemberBreakdown(props: {
  rows: Array<{ id: string; name: string; amount_cents: number }>;
  type: ReportAmountType;
  title: string;
  selectedMemberId: string | null;
  onSelect: (memberId: string) => void;
}) {
  if (props.rows.length === 0) return null;
  return (
    <div className="member-breakdown">
      <h2>{props.title}</h2>
      <div className="member-breakdown-list">
        {props.rows.map((row) => (
          <button
            className={`member-breakdown-row ${props.selectedMemberId === row.id ? "selected" : ""}`}
            key={row.id}
            type="button"
            onClick={() => props.onSelect(row.id)}
            aria-pressed={props.selectedMemberId === row.id}
          >
            <span>{row.name}</span>
            <strong className={props.type}>
              {props.type === "income" ? "+" : "-"}{formatUsd(row.amount_cents)}
            </strong>
          </button>
        ))}
      </div>
    </div>
  );
}

function SettingsView(props: {
  token: string | null;
  me: MeResponse | null;
  users: User[];
  categories: Category[];
  sources: Source[];
  tags: Tag[];
  onChanged: () => void;
  onMessage: (message: string, tone?: "ok" | "bad") => void;
}) {
  const [newToken, setNewToken] = useState("");
  const [confirmRevokeAll, setConfirmRevokeAll] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const isAdmin = props.me?.user.role === "admin";

  async function createUser(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    await submitAndReload(
      () =>
        api("/api/users", {
          token: props.token,
          method: "POST",
          body: {
            display_name: String(form.get("display_name") || ""),
            email: String(form.get("email") || ""),
            password: String(form.get("password") || ""),
            role: String(form.get("role") || "member")
          }
        }),
      formElement,
      props
    );
  }

  async function createToken(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    try {
      const result = await api<{ token: string }>("/api/automation-tokens", {
        token: props.token,
        method: "POST",
        body: {
          name: String(form.get("name") || "")
        }
      });
      setNewToken(result.token);
      formElement.reset();
      props.onChanged();
    } catch (error) {
      props.onMessage(error instanceof Error ? error.message : "创建 token 失败", "bad");
    }
  }

  async function revokeAllTokens() {
    try {
      await api("/api/automation-tokens", {
        token: props.token,
        method: "DELETE"
      });
      setConfirmRevokeAll(false);
      setNewToken("");
      props.onMessage("所有 API token 已撤销");
      props.onChanged();
    } catch (error) {
      props.onMessage(error instanceof Error ? error.message : "撤销 token 失败", "bad");
    }
  }

  async function deleteUser() {
    if (!userToDelete) return;
    try {
      await api(`/api/users/${userToDelete.id}`, {
        token: props.token,
        method: "DELETE"
      });
      props.onMessage(userToDelete.role === "bot" ? "机器人用户已删除" : "用户已删除");
      setUserToDelete(null);
      props.onChanged();
    } catch (error) {
      props.onMessage(error instanceof Error ? error.message : "删除用户失败", "bad");
    }
  }

  const realUsers = props.users.filter((user) => user.role !== "bot" && user.is_active && !isJointUser(user));
  const visibleUsers = props.users
    .filter((user) => user.is_active)
    .sort((a, b) => {
      const rank = { admin: 0, member: 1, bot: 2 };
      return rank[a.role] - rank[b.role] || a.display_name.localeCompare(b.display_name);
    });
  const canAddUser = isAdmin && realUsers.length < 2;

  return (
    <section className="section">
      <div className="shell settings-grid">
        <div>
          <h2>Members</h2>
          <div className="simple-list">
            {visibleUsers.map((user) => (
              <div className="simple-row member-row" key={user.id}>
                <span>{user.display_name}</span>
                <span className="mono">{isJointUser(user) ? "shared" : user.role}</span>
                {isAdmin && user.id !== props.me?.user.id && (
                  <button
                    className="btn icon-only btn-danger"
                    type="button"
                    onClick={() => setUserToDelete(user)}
                    aria-label={`删除${user.display_name}`}
                    title="删除用户"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            ))}
          </div>
          {canAddUser && (
            <form className="mini-form" onSubmit={createUser}>
              <input name="display_name" placeholder="第二个用户名字" required />
              <input name="email" type="email" placeholder="邮箱" required />
              <input name="password" type="password" placeholder="初始密码" minLength={8} required />
              <input type="hidden" name="role" value="member" />
              <button className="btn" type="submit">
                <UserPlus size={16} /> 添加用户
              </button>
            </form>
          )}
        </div>

        <CatalogSection
          title="分类"
          icon={<Tags size={16} />}
          items={props.categories}
          resourcePath="/api/categories"
          token={props.token}
          allowDelete
          onSubmit={(form) =>
            api("/api/categories", {
              token: props.token,
              method: "POST",
              body: {
                name: String(form.get("name")),
                color: String(form.get("color")),
                sort_order: Math.max(0, ...props.categories.map((item) => item.sort_order)) + 10,
                exclude_from_living_expense: form.get("exclude_from_living_expense") === "on"
              }
            })
          }
          onChanged={props.onChanged}
          onMessage={props.onMessage}
        />

        <CatalogSection
          title="标签"
          icon={<Tags size={16} />}
          items={props.tags}
          resourcePath="/api/tags"
          token={props.token}
          allowDelete
          onSubmit={(form) =>
            api("/api/tags", {
              token: props.token,
              method: "POST",
              body: {
                name: String(form.get("name")),
                color: String(form.get("color"))
              }
            })
          }
          onChanged={props.onChanged}
          onMessage={props.onMessage}
        />

        {isAdmin && (
          <div>
            <h2>Automation API</h2>
            <p className="section-subtitle">机器人 token 默认全功能，可读写流水、读取报表。</p>
            <form className="mini-form" onSubmit={createToken}>
              <input name="name" placeholder="OpenClaw" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} required />
              <button className="btn" type="submit">
                <Bot size={16} /> 创建 token
              </button>
              <button className="btn icon-only btn-danger" type="button" onClick={() => setConfirmRevokeAll(true)} aria-label="撤销全部 API token" title="撤销全部 API token">
                <Trash2 size={16} />
              </button>
            </form>
            {newToken && <p className="token-box">{newToken}</p>}
            {confirmRevokeAll && (
              <ConfirmModal
                title="撤销全部 API token？"
                body="所有自动化脚本和机器人 token 会立刻失效。这个操作不会删除账单，但需要重新创建 token 后脚本才能继续写账。"
                confirmLabel="确认撤销"
                onCancel={() => setConfirmRevokeAll(false)}
                onConfirm={revokeAllTokens}
              />
            )}
            {userToDelete && (
              <ConfirmModal
                title={`删除 ${userToDelete.display_name}？`}
                body={
                  userToDelete.role === "bot"
                    ? "这个机器人用户会被停用，相关 API token 会立刻失效。历史流水仍会保留。"
                    : "这个用户会被停用并退出所有设备。历史流水仍会保留。"
                }
                confirmLabel="确认删除"
                onCancel={() => setUserToDelete(null)}
                onConfirm={deleteUser}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function CatalogSection(props: {
  title: string;
  icon: React.ReactNode;
  items: Array<Category | Tag>;
  resourcePath: "/api/categories" | "/api/tags";
  token: string | null;
  allowDelete: boolean;
  onSubmit: (form: FormData) => Promise<unknown>;
  onChanged: () => void;
  onMessage: (message: string, tone?: "ok" | "bad") => void;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    await submitAndReload(() => props.onSubmit(new FormData(formElement)), formElement, props);
  }

  async function updateItem(item: Category | Tag, form: HTMLFormElement) {
    const formData = new FormData(form);
    try {
      await api(`${props.resourcePath}/${item.id}`, {
        token: props.token,
        method: "PATCH",
        body: {
          name: String(formData.get("name")),
          color: String(formData.get("color")),
          sort_order: "sort_order" in item ? item.sort_order : undefined,
          exclude_from_living_expense:
            "exclude_from_living_expense" in item
              ? formData.get("exclude_from_living_expense") === "on"
              : undefined
        }
      });
      setEditingId(null);
      props.onMessage("已保存");
      props.onChanged();
    } catch (error) {
      props.onMessage(error instanceof Error ? error.message : "保存失败", "bad");
    }
  }

  async function deleteItem(item: Category | Tag) {
    try {
      await api(`${props.resourcePath}/${item.id}`, {
        token: props.token,
        method: "DELETE"
      });
      props.onMessage(props.resourcePath === "/api/categories" ? "已归档分类" : "已删除标签");
      props.onChanged();
    } catch (error) {
      props.onMessage(error instanceof Error ? error.message : "删除失败", "bad");
    }
  }

  return (
    <div className="settings-section">
      <h2>
        {props.icon} {props.title}
      </h2>
      <div className="simple-list">
        {props.items.map((item) =>
          editingId === item.id ? (
            <form
              className={`catalog-edit ${item.archived_at ? "archived" : ""}`}
              key={item.id}
              onSubmit={(event) => {
                event.preventDefault();
                void updateItem(item, event.currentTarget);
              }}
            >
              <input name="name" defaultValue={item.name} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} required />
              <ColorPalette name="color" defaultValue={item.color} />
              <div className="catalog-actions">
                {"exclude_from_living_expense" in item && (
                  <label className="living-expense-toggle" title="勾选后，这个分类不会计入月报生活费">
                    <input
                      name="exclude_from_living_expense"
                      type="checkbox"
                      defaultChecked={Boolean(item.exclude_from_living_expense)}
                    />
                    <span>生活费</span>
                  </label>
                )}
                <button className="btn icon-only" type="submit" aria-label="保存" title="保存">
                  <Check size={16} />
                </button>
                <button className="btn icon-only" type="button" onClick={() => setEditingId(null)} aria-label="取消" title="取消">
                  <X size={16} />
                </button>
                {props.allowDelete && (
                  <button
                    className="btn icon-only btn-danger"
                    type="button"
                    onClick={() => void deleteItem(item)}
                    aria-label={`删除${item.name}`}
                    title={props.resourcePath === "/api/categories" ? "归档分类" : "删除标签"}
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </form>
          ) : (
            <div className={`simple-row catalog-row ${item.archived_at ? "archived" : ""}`} key={item.id}>
              <span className="color-dot" style={{ background: item.color }} />
              <span>{item.name}</span>
              <button
                className="icon-btn"
                type="button"
                onClick={() => setEditingId(item.id)}
                aria-label={`编辑${item.name}`}
                title="编辑"
              >
                <Pencil size={15} />
              </button>
            </div>
          )
        )}
      </div>
      <form className="mini-form" onSubmit={submit}>
        <input name="name" placeholder={`${props.title}名`} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} required />
        <ColorPalette name="color" defaultValue={props.resourcePath === "/api/categories" ? "#ff4fa3" : "#ff8ac4"} />
        <button className="btn icon-only" type="submit" aria-label={`添加${props.title}`} title={`添加${props.title}`}>
          <Plus size={16} />
        </button>
      </form>
    </div>
  );
}

function ColorPalette({ name, defaultValue }: { name: string; defaultValue: string }) {
  const [selected, setSelected] = useState(colorOptions.includes(defaultValue) ? defaultValue : colorOptions[0]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  return (
    <div className={`color-picker ${open ? "open" : ""}`} ref={ref}>
      <input type="hidden" name={name} value={selected} />
      <button type="button" className="color-trigger" onClick={() => setOpen((current) => !current)}>
        <span className="color-dot large" style={{ background: selected }} aria-hidden="true" />
        <span className="sr-only">选择颜色</span>
      </button>
      {open && (
        <div className="color-palette">
          {colorOptions.map((color) => (
            <button
              key={color}
              className={`swatch ${color === selected ? "selected" : ""}`}
              style={{ background: color }}
              title={color}
              type="button"
              onClick={() => {
                setSelected(color);
                setOpen(false);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FilePicker({
  name,
  multiple = false,
  label,
  onFiles
}: {
  name: string;
  multiple?: boolean;
  label: string;
  onFiles?: (files: File[]) => void | Promise<void>;
}) {
  const [fileText, setFileText] = useState("未选择文件");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const form = inputRef.current?.form;
    if (!form) return;
    const reset = () => setFileText("未选择文件");
    form.addEventListener("reset", reset);
    return () => form.removeEventListener("reset", reset);
  }, []);

  return (
    <div className="file-picker">
      <label className="file-trigger">
        <Upload size={16} />
        {label}
        <input
          ref={inputRef}
          className="file-input-hidden"
          name={name}
          type="file"
          multiple={multiple}
          onChange={(event) => {
            const files = Array.from(event.currentTarget.files ?? []);
            if (files.length === 0) setFileText("未选择文件");
            else if (files.length === 1) setFileText(files[0].name);
            else setFileText(`${files.length} 个文件`);
            if (files.length > 0 && onFiles) {
              void Promise.resolve(onFiles(files)).then(() => {
                event.currentTarget.value = "";
                setFileText("未选择文件");
              });
            }
          }}
        />
      </label>
      <span className="file-name">{fileText}</span>
    </div>
  );
}

function useEscape(handler: () => void, enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") handler();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handler, enabled]);
}

async function submitAndReload(
  submit: () => Promise<unknown>,
  form: HTMLFormElement,
  handlers: { onChanged: () => void; onMessage: (message: string, tone?: "ok" | "bad") => void }
) {
  try {
    await submit();
    form.reset();
    handlers.onMessage("已保存");
    handlers.onChanged();
  } catch (error) {
    handlers.onMessage(error instanceof Error ? error.message : "保存失败", "bad");
  }
}

function SummaryStrip({ report }: { report: MonthlyReport | null }) {
  const items = [
    ["收入", report?.totals.income_cents ?? 0, "income"],
    ["支出", report?.totals.expense_cents ?? 0, "expense"],
    ["生活费", report?.totals.living_expense_cents ?? 0, "expense"],
    ["结余", report?.totals.balance_cents ?? 0, "balance"]
  ] as const;
  return (
    <div className="summary-strip">
      {items.map(([label, cents, tone]) => (
        <div className="summary-cell" key={label}>
          <span className="mono">{label}</span>
          <strong className={tone}>{formatUsd(cents)}</strong>
        </div>
      ))}
    </div>
  );
}

function TransactionList({
  transactions,
  title,
  onSelect,
  emptyLabel = "还没有记录。"
}: {
  transactions: Transaction[];
  title: string;
  onSelect?: (transaction: Transaction) => void;
  emptyLabel?: string;
}) {
  return (
    <div className="transaction-block">
      {title && <h2>{title}</h2>}
      <div className="transaction-list">
        {transactions.length === 0 && <p className="muted">{emptyLabel}</p>}
        {transactions.map((item) => (
          <button
            className={`transaction-row ${onSelect ? "clickable" : ""}`}
            key={item.id}
            type="button"
            onClick={() => onSelect?.(item)}
          >
            <div>
              <div className="transaction-title">
                <span className="color-dot" style={{ background: item.category_color }} />
                <strong>{item.name || item.category_name}</strong>
              </div>
              <p>
                {item.occurred_on} · {item.spender_name} · {item.source_name} · {item.category_name}
              </p>
              {item.tags.length > 0 && (
                <div className="tag-line">
                  {item.tags.map((tag) => (
                    <span key={tag.id}>{tag.name}</span>
                  ))}
                </div>
              )}
            </div>
            <strong className={item.type}>{item.type === "income" ? "+" : "-"}{formatUsd(item.amount_cents)}</strong>
          </button>
        ))}
      </div>
    </div>
  );
}

function TransactionDetailModal(props: {
  transaction: Transaction;
  token: string | null;
  users: User[];
  categories: Category[];
  sources: Source[];
  tags: Tag[];
  onClose: () => void;
  onChanged: (nextTransactionId?: string) => void | Promise<void>;
  onDeleted: () => void | Promise<void>;
  onMessage: (message: string, tone?: "ok" | "bad") => void;
}) {
  const [transaction, setTransaction] = useState(props.transaction);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageThumbs, setImageThumbs] = useState<Record<string, string>>({});

  useEscape(props.onClose, !confirmDelete && !previewAttachment);

  useEffect(() => {
    setTransaction(props.transaction);
    setEditing(false);
    void loadAttachments(props.transaction.id);
  }, [props.transaction.id]);

  useEffect(() => {
    if (!props.token) return;
    const imageAttachments = attachments.filter(isImageAttachment);
    const urls: string[] = [];
    let cancelled = false;
    if (imageAttachments.length === 0) {
      setImageThumbs({});
      return;
    }
    void Promise.all(
      imageAttachments.map(async (attachment) => {
        const response = await fetch(`/api/attachments/${attachment.id}/download`, {
          headers: { Authorization: `Bearer ${props.token}` }
        });
        if (!response.ok) return null;
        const url = URL.createObjectURL(await response.blob());
        urls.push(url);
        return [attachment.id, url] as const;
      })
    )
      .then((entries) => {
        if (!cancelled) setImageThumbs(Object.fromEntries(entries.filter(Boolean) as Array<[string, string]>));
      })
      .catch(() => {
        if (!cancelled) setImageThumbs({});
      });
    return () => {
      cancelled = true;
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [attachments, props.token]);

  useEffect(() => {
    if (!previewAttachment || !props.token) {
      setPreviewUrl(null);
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    void fetch(`/api/attachments/${previewAttachment.id}/download`, {
      headers: { Authorization: `Bearer ${props.token}` }
    })
      .then((response) => {
        if (!response.ok) throw new Error("图片加载失败");
        return response.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      })
      .catch((error) => props.onMessage(error instanceof Error ? error.message : "图片加载失败", "bad"));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [previewAttachment, props.token]);

  async function loadAttachments(transactionId = transaction.id) {
    if (!props.token) return;
    try {
      setAttachments(
        await api<Attachment[]>(`/api/transactions/${transactionId}/attachments`, {
          token: props.token
        })
      );
    } catch (error) {
      props.onMessage(error instanceof Error ? error.message : "附件加载失败", "bad");
    }
  }

  async function loadTransactionVersion(transactionId: string) {
    if (!props.token) return;
    const fresh = await api<Transaction>(`/api/transactions/${transactionId}`, { token: props.token });
    setTransaction(fresh);
    await loadAttachments(fresh.id);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      const result = await api<{ id: string }>(`/api/transactions/${transaction.id}`, {
        token: props.token,
        method: "PATCH",
        body: {
          type: String(form.get("type")),
          occurred_on: String(form.get("occurred_on")),
          amount_cents: parseUsdToCents(String(form.get("amount"))),
          category_id: String(form.get("category_id")),
          source_id: String(form.get("source_id")),
          spender_user_id: String(form.get("spender_user_id")),
          name: String(form.get("name") || "") || null,
          note: String(form.get("note") || "") || null,
          tag_ids: form.getAll("tag_ids").map(String)
        }
      });
      setEditing(false);
      await loadTransactionVersion(result.id);
      await props.onChanged(result.id);
      window.dispatchEvent(new Event("our-money:transactions-changed"));
      props.onMessage("流水已更新");
    } catch (error) {
      props.onMessage(error instanceof Error ? error.message : "更新失败", "bad");
    }
  }

  async function uploadAttachment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const file = form.get("file");
    if (!(file instanceof File) || file.size === 0) return;
    await uploadAttachmentFiles([file]);
    formElement.reset();
  }

  async function uploadAttachmentFiles(files: File[]) {
    const file = files[0];
    if (!file || file.size === 0) return;
    const payload = new FormData();
    payload.set("file", file);
    try {
      const result = await api<{ id: string; transaction_id: string }>(`/api/transactions/${transaction.id}/attachments`, {
        token: props.token,
        method: "POST",
        body: payload
      });
      await loadTransactionVersion(result.transaction_id);
      await props.onChanged(result.transaction_id);
      props.onMessage("附件已上传");
    } catch (error) {
      props.onMessage(error instanceof Error ? error.message : "附件上传失败", "bad");
    }
  }

  async function downloadAttachment(attachment: Attachment) {
    if (!props.token) return;
    try {
      const response = await fetch(`/api/attachments/${attachment.id}/download`, {
        headers: { Authorization: `Bearer ${props.token}` }
      });
      if (!response.ok) throw new Error("下载失败");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.file_name;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      props.onMessage(error instanceof Error ? error.message : "下载失败", "bad");
    }
  }

  async function deleteAttachment(attachment: Attachment) {
    try {
      const result = await api<{ ok: boolean; transaction_id: string }>(`/api/attachments/${attachment.id}`, {
        token: props.token,
        method: "DELETE"
      });
      await loadTransactionVersion(result.transaction_id);
      await props.onChanged(result.transaction_id);
      props.onMessage("附件已删除");
    } catch (error) {
      props.onMessage(error instanceof Error ? error.message : "删除附件失败", "bad");
    }
  }

  async function deleteTransaction() {
    try {
      await api(`/api/transactions/${transaction.id}`, {
        token: props.token,
        method: "DELETE"
      });
      props.onMessage("流水已删除");
      window.dispatchEvent(new Event("our-money:transactions-changed"));
      await props.onDeleted();
    } catch (error) {
      props.onMessage(error instanceof Error ? error.message : "删除失败", "bad");
    }
  }

  const isInactiveVersion = transaction.status === "inactive";

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={props.onClose}>
      <section className="transaction-modal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-head">
          <div>
            <p className="mono">流水详情</p>
            <h3>{transaction.name || transaction.category_name}</h3>
          </div>
          <button className="icon-btn" type="button" onClick={props.onClose} aria-label="关闭">
            <X size={18} />
          </button>
        </div>

        {!editing ? (
          <div className="detail-grid">
            {isInactiveVersion && (
              <p className="warning wide">
                <AlertTriangle size={16} /> 这是一条历史版本，已不参与搜索默认结果和月报统计。
              </p>
            )}
            <DetailItem
              label="金额"
              value={`${transaction.type === "income" ? "+" : "-"}${formatUsd(transaction.amount_cents)}`}
              tone={transaction.type}
            />
            <DetailItem label="日期" value={transaction.occurred_on} />
            <DetailItem label="谁花/收" value={transaction.spender_name} />
            <DetailItem label="分类" value={transaction.category_name} />
            <DetailItem label="来源" value={transaction.source_name} />
            <DetailItem label="创建方式" value={transaction.created_via === "api" ? "Bot / API" : "网页"} />
            {transaction.note && <DetailItem label="备注" value={transaction.note} wide />}
            {transaction.tags.length > 0 && (
              <div className="detail-item wide">
                <span>标签</span>
                <div className="tag-line">
                  {transaction.tags.map((tag) => (
                    <span key={tag.id}>{tag.name}</span>
                  ))}
                </div>
              </div>
            )}
            {!isInactiveVersion && (
              <>
                <div className="detail-action-separator" />
                <div className="detail-actions wide">
                  <button className="btn btn-primary icon-only detail-action" type="button" onClick={() => setEditing(true)} aria-label="修改这笔流水" title="修改这笔流水">
                    <Pencil size={17} />
                  </button>
                  <button className="btn btn-danger icon-only detail-action" type="button" onClick={() => setConfirmDelete(true)} aria-label="删除此记录" title="删除此记录">
                    <Trash2 size={17} />
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <form className="form-grid" onSubmit={submit}>
            <p className="warning">
              <AlertTriangle size={16} /> 修改过去的流水会立刻影响对应月份的月报和结余。
            </p>
            <div className="field-row">
              <label>
                类型
                <select name="type" defaultValue={transaction.type} required>
                  <option value="expense">支出</option>
                  <option value="income">收入</option>
                </select>
              </label>
              <label>
                金额
                <input name="amount" inputMode="decimal" defaultValue={(transaction.amount_cents / 100).toFixed(2)} required />
              </label>
            </div>
            <div className="field-row">
              <label>
                日期
                <input name="occurred_on" type="date" defaultValue={transaction.occurred_on} required />
              </label>
              <label>
                谁花/收
                <select name="spender_user_id" defaultValue={transaction.spender_user_id} required>
                  {props.users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.display_name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="field-row">
              <label>
                分类
                <select name="category_id" defaultValue={transaction.category_id} required>
                  {props.categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                来源
                <select name="source_id" defaultValue={transaction.source_id} required>
                  {props.sources.map((source) => (
                    <option key={source.id} value={source.id}>
                      {source.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label>
              名字
              <input name="name" defaultValue={transaction.name ?? ""} />
            </label>
            <label>
              备注
              <textarea name="note" rows={3} defaultValue={transaction.note ?? ""} />
            </label>
            <fieldset className="tag-field">
              <legend>标签</legend>
              {props.tags.map((tag) => (
                <label key={tag.id} className="check-chip">
                  <input
                    type="checkbox"
                    name="tag_ids"
                    value={tag.id}
                    defaultChecked={transaction.tags.some((selected) => selected.id === tag.id)}
                  />
                  {tag.name}
                </label>
              ))}
            </fieldset>
            <div className="modal-actions">
              <button className="btn btn-primary" type="submit">
                保存修改
              </button>
              <button className="btn" type="button" onClick={() => setEditing(false)}>
                取消
              </button>
            </div>
          </form>
        )}

        <div className="attachment-panel">
          <h2>
            <Upload size={16} /> 附件
          </h2>
          {!isInactiveVersion && (
            <div className="attachment-form">
              <FilePicker name="file" label="选择附件" onFiles={uploadAttachmentFiles} />
            </div>
          )}
          <div className="simple-list">
            {attachments.length === 0 && <p className="muted">还没有附件。</p>}
            {attachments.map((attachment) => (
              <div className="attachment-row" key={attachment.id}>
                {isImageAttachment(attachment) ? (
                  <button
                    className="attachment-preview"
                    type="button"
                    onClick={() => setPreviewAttachment(attachment)}
                  >
                    {imageThumbs[attachment.id] ? (
                      <img className="image-thumb" src={imageThumbs[attachment.id]} alt="" />
                    ) : (
                      <span className="image-thumb" aria-hidden="true" />
                    )}
                    <span className="attachment-name">{attachment.file_name}</span>
                  </button>
                ) : (
                  <span className="attachment-name">{attachment.file_name}</span>
                )}
                <span className="mono attachment-size">{formatBytes(attachment.byte_size)}</span>
                <div className="attachment-actions">
                  {isImageAttachment(attachment) && (
                    <button className="icon-btn" type="button" onClick={() => setPreviewAttachment(attachment)} title="预览">
                      <Expand size={15} />
                    </button>
                  )}
                  <button className="icon-btn" type="button" onClick={() => void downloadAttachment(attachment)} title="下载">
                    <Download size={15} />
                  </button>
                  {!isInactiveVersion && (
                    <button className="icon-btn danger" type="button" onClick={() => void deleteAttachment(attachment)} title="删除">
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </div>
              ))}
          </div>
        </div>

        <div className="activity-panel">
          <h2>Activity</h2>
          <div className="activity-list">
            <div>
              <span>创建</span>
              <strong>{transaction.created_by_name} · {formatDateTime(transaction.created_at)}</strong>
            </div>
            <div>
              <span>最近更新</span>
              <strong>{transaction.updated_by_name} · {formatDateTime(transaction.updated_at)}</strong>
            </div>
            {transaction.previous_transaction_id && (
              <div>
                <span>版本</span>
                <strong>由上一版修改而来</strong>
              </div>
            )}
            {isInactiveVersion && (
              <div>
                <span>状态</span>
                <strong>历史版本</strong>
              </div>
            )}
          </div>
        </div>
        {confirmDelete && (
          <ConfirmModal
            title="删除这笔流水？"
            body="这个操作会立即影响对应月份的月报和结余。删除后无法在界面里恢复。"
            confirmLabel="确认删除"
            onCancel={() => setConfirmDelete(false)}
            onConfirm={deleteTransaction}
          />
        )}
        {previewAttachment && (
          <ImagePreviewModal
            attachment={previewAttachment}
            imageUrl={previewUrl}
            onClose={() => setPreviewAttachment(null)}
            onDownload={() => void downloadAttachment(previewAttachment)}
          />
        )}
      </section>
    </div>
  );
}

function ConfirmModal(props: {
  title: string;
  body: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
  tone?: "danger" | "update";
}) {
  const tone = props.tone ?? "danger";
  useEscape(props.onCancel);
  return (
    <div className="confirm-layer" role="presentation" onMouseDown={props.onCancel}>
      <section className={`confirm-modal ${tone}`} role="alertdialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
        <div className={`confirm-icon ${tone}`}>
          <AlertTriangle size={22} />
        </div>
        <h3>{props.title}</h3>
        <p>{props.body}</p>
        <div className="modal-actions">
          <button className="btn" type="button" onClick={props.onCancel}>
            取消
          </button>
          <button className={`btn ${tone === "danger" ? "btn-danger" : "btn-primary"}`} type="button" onClick={() => void props.onConfirm()}>
            {props.confirmLabel}
          </button>
        </div>
      </section>
    </div>
  );
}

function ImagePreviewModal(props: {
  attachment: Attachment;
  imageUrl: string | null;
  onClose: () => void;
  onDownload: () => void;
}) {
  useEscape(props.onClose);
  return (
    <div className="image-preview-layer" role="presentation" onMouseDown={props.onClose}>
      <section className="image-preview-modal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-head">
          <div>
            <p className="mono">图片预览</p>
            <h3>{props.attachment.file_name}</h3>
          </div>
          <div className="preview-actions">
            <button className="icon-btn" type="button" onClick={props.onDownload} aria-label="下载图片" title="下载">
              <Download size={18} />
            </button>
            <button className="icon-btn" type="button" onClick={props.onClose} aria-label="关闭预览" title="关闭">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="image-stage">
          {props.imageUrl ? <img src={props.imageUrl} alt={props.attachment.file_name} /> : <p className="muted">图片加载中...</p>}
        </div>
      </section>
    </div>
  );
}

function DetailItem({
  label,
  value,
  wide = false,
  tone
}: {
  label: string;
  value: string;
  wide?: boolean;
  tone?: "income" | "expense";
}) {
  return (
    <div className={`detail-item ${wide ? "wide" : ""}`}>
      <span>{label}</span>
      <strong className={tone ?? ""}>{value}</strong>
    </div>
  );
}

function groupTransactionsByMonth(transactions: Transaction[]) {
  const map = new Map<string, { month: string; income: number; expense: number; items: Transaction[] }>();
  for (const transaction of transactions) {
    const month = transaction.occurred_on.slice(0, 7);
    const group = map.get(month) ?? { month, income: 0, expense: 0, items: [] };
    if (transaction.type === "income") group.income += transaction.amount_cents;
    else group.expense += transaction.amount_cents;
    group.items.push(transaction);
    map.set(month, group);
  }
  return [...map.values()].sort((a, b) => b.month.localeCompare(a.month));
}

function formatMonthLabel(month: string) {
  const [year, rawMonth] = month.split("-");
  return `${year}年${Number(rawMonth)}月`;
}

function daysAgo(days: number) {
  return daysAgoInTimeZone(days);
}

function pathToView(pathname: string): View {
  const clean = pathname.replace(/\/+$/, "") || "/";
  if (clean === "/ledger" || clean === "/records" || clean.startsWith("/records/")) return "ledger";
  if (clean === "/search") return "search";
  if (clean === "/report" || clean.startsWith("/report/")) return "report";
  if (clean === "/settings") return "settings";
  return "entry";
}

function pathToReportDrilldown(pathname: string, search: string) {
  const match = pathname.match(/^\/report\/(\d{4}-\d{2})\/(category|source|member)\/([^/]+)\/?$/);
  if (!match) return null;
  const params = new URLSearchParams(search);
  const type: ReportAmountType = params.get("type") === "income" ? "income" : "expense";
  return {
    month: match[1],
    dimension: match[2] as ReportDimension,
    id: decodeURIComponent(match[3]),
    type
  };
}

function pathToTransactionId(pathname: string) {
  const match = pathname.match(/^\/records\/([^/]+)\/?$/);
  return match ? decodeURIComponent(match[1]) : null;
}

function viewToPath(view: View) {
  return navItems.find((item) => item.view === view)?.path ?? "/";
}

function reportDrilldownPath(drilldown: ReportDrilldown) {
  return `/report/${drilldown.month}/${drilldown.dimension}/${encodeURIComponent(drilldown.id)}?type=${drilldown.type}`;
}

function reportDimensionLabel(dimension: ReportDimension) {
  if (dimension === "category") return "分类";
  if (dimension === "source") return "来源";
  return "成员";
}

function reportDimensionParam(dimension: ReportDimension) {
  if (dimension === "category") return "category_id";
  if (dimension === "source") return "source_id";
  return "spender_user_id";
}

function getReportRows(report: MonthlyReport | null, dimension: ReportDimension) {
  if (!report) return [];
  if (dimension === "category") return report.by_category;
  if (dimension === "source") return report.by_source;
  return report.by_member;
}

function summarizeByMember(transactions: Transaction[]) {
  const map = new Map<string, { id: string; name: string; amount_cents: number }>();
  for (const transaction of transactions) {
    const current = map.get(transaction.spender_user_id) ?? {
      id: transaction.spender_user_id,
      name: transaction.spender_name,
      amount_cents: 0
    };
    current.amount_cents += transaction.amount_cents;
    map.set(transaction.spender_user_id, current);
  }
  return [...map.values()].sort((a, b) => b.amount_cents - a.amount_cents || a.name.localeCompare(b.name));
}

function monthBounds(month: string) {
  const [year, rawMonth] = month.split("-").map(Number);
  const lastDay = new Date(year, rawMonth, 0).getDate();
  return { from: `${month}-01`, to: `${month}-${String(lastDay).padStart(2, "0")}` };
}

async function fetchReportTransactions(token: string, drilldown: ReportDrilldown) {
  const { from, to } = monthBounds(drilldown.month);
  const rows: Transaction[] = [];
  const limit = 100;
  for (let offset = 0; offset < 1000; offset += limit) {
    const params = new URLSearchParams({
      from,
      to,
      status: "active",
      type: drilldown.type,
      limit: String(limit),
      offset: String(offset)
    });
    params.set(reportDimensionParam(drilldown.dimension), drilldown.id);
    const page = await api<Transaction[]>(`/api/transactions?${params.toString()}`, { token });
    rows.push(...page);
    if (page.length < limit) break;
  }
  return rows;
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function isImageAttachment(attachment: Attachment) {
  return Boolean(attachment.content_type?.startsWith("image/"));
}

function isJointUser(user: User) {
  return user.display_name.trim().toLowerCase() === "dkt & cindy";
}

function ReportTable({
  title,
  dimension,
  rows,
  onOpen
}: {
  title: string;
  dimension: ReportDimension;
  rows: { id: string; name: string; income_cents: number; expense_cents: number }[];
  onOpen: (dimension: ReportDimension, id: string, type: ReportAmountType) => void;
}) {
  const sorted = useMemo(
    () => [...rows].sort((a, b) => b.income_cents + b.expense_cents - (a.income_cents + a.expense_cents)),
    [rows]
  );
  return (
    <div className="report-table">
      <h2>{title}</h2>
      {sorted.map((row) => (
        <div className="report-row" key={row.id}>
          <span>{row.name}</span>
          <div className="report-row-amounts">
            {row.income_cents > 0 && (
              <button type="button" className="report-amount income" onClick={() => onOpen(dimension, row.id, "income")}>
                +{formatUsd(row.income_cents)}
              </button>
            )}
            {row.expense_cents > 0 && (
              <button type="button" className="report-amount expense" onClick={() => onOpen(dimension, row.id, "expense")}>
                -{formatUsd(row.expense_cents)}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
